// 리포트 메뉴 — 기업·산업·테마 분석 리포트 (버핏스코어 랜딩과 같은 뼈대).
//
// 랜딩 = 검색창(상장·비상장 매칭) + 페이지 설명 + 완성 샘플.
// 기업을 고르면 서버에서 **실제로 생성**한다(collect → 조판 → PDF, ~30초):
// /agent/report2/generate → status 폴링 → 뷰어(PDF + 목차 + 추가 요청 대화).
// 샘플 카드는 뷰어 없이 바로 PDF 모달만 연다. 키워드는 산업·테마 경로(베타 안내).
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  reportGenerate, reportStatus, searchReportCompany,
  type ReportCompanyHit, type ReportResult,
} from "../lib/labApi";
import {
  KIND_DEEP, KIND_LABEL, SAMPLES, recordReportAsk, recordReportRecent,
  reportRecentList, samplePages, samplePdf, type ReportKind, type ReportRecent, type ReportSample,
} from "../lib/reportMeta";
import { Chip } from "../ui/Chip";
import { recordLabFile } from "../lib/labFiles";
import { isMobile } from "../lib/useMobileSide";
import { PdfViewer } from "../ui/PdfViewer";
import "../styles/report.css";

type Screen = "home" | "gen" | "view";

/** 뷰어가 그리는 문서 — 샘플(정적 자산)과 서버 생성물을 한 형태로 다룬다 */
interface ViewDoc {
  subject: string;
  kind: ReportKind;
  pages: number;
  asof: string;
  toc: [string, string][];
  pdfUrl: string;       // 미리보기(inline) 주소
  saveUrl: string;      // 저장(attachment) 주소
  cover?: string;       // 모바일 표지 썸네일 (샘플만 있음)
}

/** 생성 단계 — 서버(report2/service)의 step 문자열과 같은 순서 */
const GEN_STEPS: [string, string][] = [
  ["재무 데이터 수집", "감사보고서 기반 5개년 · 시세"],
  ["서사 생성 · 검증", "수치 검증을 통과한 문장만 수록"],
  ["지표 산출 · 조판", "파생 지표 계산 · 차트 · 표"],
  ["PDF 렌더", "증권사 리포트 형식 조판"],
  ["저장", "다운로드 링크 준비"],
];

// 랜딩 "만드는 방식" 4장 — 버핏 랜딩의 채점 방식 카드와 같은 문법
const HOW: { t: string; d: string; icon: ReactNode }[] = [
  { t: "수치는 전부 기계 산출",
    d: "DeepSearch Finorma에서 재무를 직접 계산하고, 모든 도표에 출처를 답니다. 지어낸 숫자가 없습니다.",
    icon: <><rect x="4" y="2" width="16" height="20" rx="2" /><path d="M8 6h8" /><path d="M8 12h.01M12 12h.01M16 12h.01M8 16h.01M12 16h.01M16 16h.01" /></> },
  { t: "상장·비상장 모두",
    d: "코스피·코스닥은 시세·배수까지, 비상장 외감법인은 감사보고서 재무로 만듭니다.",
    icon: <><path d="M3 21h18" /><path d="M5 21V7l8-4v18" /><path d="M19 21V11l-6-4" /></> },
  { t: "불리한 수치도 그대로",
    d: "수집하지 못한 데이터는 커버리지 표에 '미수집'으로 명시합니다. 좋은 이야기만 쓰지 않습니다.",
    icon: <><path d="M12 9v4" /><path d="M12 17h.01" /><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /></> },
  { t: "증권사 리포트 형식의 PDF",
    d: "표지부터 커버리지까지 실물 리포트 조판을 따릅니다. 1분 안에 완성되고, 저장해 공유할 수 있습니다.",
    icon: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M8 13h8" /><path d="M8 17h5" /></> },
];

// 검색 시연 칩 풀 — 접속마다 랜덤 4개. 기업(실 매칭)과 키워드(테마·산업 경로)를 섞는다
const CHIP_POOL = [
  "한미반도체", "리노공업", "무신사", "크래프톤", "클래시스", "산일전기",
  "파마리서치", "알테오젠", "이커머스", "영업이익률 30% 클럽", "반도체 장비",
];

function docFromResult(r: ReportResult): ViewDoc {
  // S3 영구본이 있으면 그것을 쓴다(파드 로컬 토큰은 24시간·재배포까지만 산다).
  // 미리보기는 inline 강제 프록시(report-view)로 — S3 직링크는 다운로드로 떨어진다.
  if (r.url) {
    return { subject: r.subject, kind: (r.kind as ReportKind) || "corp", pages: r.pages,
             asof: r.asof, toc: r.toc || [],
             pdfUrl: `/agent/report-view?url=${encodeURIComponent(r.url)}`, saveUrl: r.url };
  }
  return { subject: r.subject, kind: (r.kind as ReportKind) || "corp", pages: r.pages,
           asof: r.asof, toc: r.toc || [],
           pdfUrl: `/agent/report/${r.token}?inline=1`, saveUrl: `/agent/report/${r.token}` };
}

/** '내 리포트'에 저장된 이전 생성물 → 뷰어 문서 (url 이 있을 때만 성립) */
function docFromRecent(r: ReportRecent): ViewDoc | null {
  if (!r.url) return null;
  return { subject: r.subject, kind: r.kind, pages: r.pages || 0,
           asof: r.asof || "", toc: r.toc || [],
           pdfUrl: `/agent/report-view?url=${encodeURIComponent(r.url)}`, saveUrl: r.url };
}

export default function ReportPage() {
  const [screen, setScreen] = useState<Screen>("home");
  const [doc, setDoc] = useState<ViewDoc | null>(null);
  const [recents, setRecents] = useState<ReportRecent[]>(reportRecentList);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [preview, setPreview] = useState<ReportSample | null>(null);
  const [mobile] = useState(isMobile);
  const [chips] = useState<string[]>(() => [...CHIP_POOL].sort(() => Math.random() - 0.5).slice(0, 4));

  // ── 검색 — 입력 후 250ms 디바운스로 실 API 매칭 (상장·비상장) ──
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<ReportCompanyHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [pendingName, setPendingName] = useState("");
  useEffect(() => {
    const v = q.trim();
    if (!v) { setHits([]); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(() => {
      searchReportCompany(v)
        .then((items) => { setHits(items.slice(0, 6)); })
        .catch(() => { setHits([]); })
        .finally(() => { setSearching(false); });
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  // ── 실제 생성 — 서버 태스크 + 폴링 ──
  const [genName, setGenName] = useState("");
  const [genStep, setGenStep] = useState(0);
  const [genError, setGenError] = useState("");
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  useEffect(() => stopPoll, []);

  const startRealGen = async (h: ReportCompanyHit) => {
    setQ(""); setHits([]); setPendingName("");
    setGenName(h.name); setGenStep(0); setGenError(""); setScreen("gen");
    let tid = "";
    try {
      tid = await reportGenerate(h.symbol, h.name, h.listed);
    } catch (e) {
      setGenError(e instanceof Error ? e.message : "생성 시작에 실패했어요"); return;
    }
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const st = await reportStatus(tid);
        const idx = GEN_STEPS.findIndex(([t]) => t === st.step);
        if (idx >= 0) setGenStep(idx);
        if (st.error) { setGenError(st.error); stopPoll(); return; }
        if (st.done && st.result) {
          stopPoll();
          setGenStep(GEN_STEPS.length);
          recordReportRecent({ key: "", kind: "corp", subject: st.result.subject,
                               symbol: st.result.symbol, listed: st.result.listed,
                               url: st.result.url || "", pages: st.result.pages,
                               asof: st.result.asof, toc: st.result.toc || [] });
          // 문서함 병합 — 리포트 메뉴 산출물은 대화 밖에서 생기므로 lab 기록소에 남긴다
          if (st.result.url) {
            recordLabFile({ url: st.result.url,
                            label: `${st.result.subject}_기업조사리포트_${st.result.asof}.pdf`,
                            kind: "report", feature: "기업조사 리포트", page: "/report",
                            company: st.result.subject });
          }
          setRecents(reportRecentList());
          setDoc(docFromResult(st.result));
          setThread([]); setScreen("view"); setPdfOpen(false);
          window.scrollTo(0, 0);
        }
      } catch (e) {
        setGenError(e instanceof Error ? e.message : "상태 조회에 실패했어요"); stopPoll();
      }
    }, 1200);
  };

  const pickCompany = (h: ReportCompanyHit) => { void startRealGen(h); };

  // 키워드 경로 — 샘플 주제와 닿으면 바로 열고, 아니면 안내(요청 키워드 수집)
  const pickKeyword = (kw: string) => {
    setQ(""); setHits([]);
    const k = kw.replace(/\s+/g, "");
    const sm = SAMPLES.find((x) => x.kind !== "corp" && (
      x.subject.replace(/\s+/g, "").includes(k) || (k.includes("커머스") && x.key === "ecom")
      || ((k.includes("이익률") || k.includes("30%")) && x.key === "club")));
    if (sm) { setPreview(sm); return; }
    recordReportAsk("keyword", kw);
    setPendingName(`『${kw}』 키워드`);
  };

  const openPreview = (sm: ReportSample) => { setPreview(sm); setPendingName(""); };

  // ── 뷰어 대화 — 서버 연결 전: 요청을 기록하고 그대로 안내한다 ──
  const [thread, setThread] = useState<{ who: "user" | "agent"; text: string }[]>([]);
  const [ask, setAsk] = useState("");
  const sendAsk = (text: string) => {
    const v = text.trim();
    if (!v || !doc) return;
    recordReportAsk(doc.subject, v);
    setThread((th) => [...th, { who: "user", text: v }]);
    setAsk("");
    setTimeout(() => {
      setThread((th) => [...th, {
        who: "agent",
        text: "요청을 기록해 뒀어요. 리포트 내용을 대화로 추가·수정하는 기능은 다음 업데이트에서 열려요 — 이 요청은 그때 우선 반영할게요.",
      }]);
    }, 700);
  };

  // ── 랜딩 ──
  const heroCover = samplePages("krafton", 1)[0];
  const home = (
    <div className="rpt-wrap">
      <div className="rpt-hero">
        <div>
          <div className="rpt-eyebrow">ANALYSIS REPORT</div>
          <h1>기업·산업·테마를 한 편의 리포트로</h1>
          <p>궁금한 기업이나 키워드를 검색하면 딥서치가 데이터를 수집·검증해
          증권사 리포트 형식의 PDF로 만들어 드립니다. 상장·비상장 모두 1분 안에 완성됩니다.</p>
        </div>
        {heroCover && (
          <button className="rpt-hero-cover" type="button" onClick={() => openPreview(SAMPLES[0])} aria-label="완성 샘플 보기">
            <img src={heroCover} alt="완성 리포트 표지 예시" />
          </button>
        )}
      </div>

      <div className="rpt-hsearch">
        <div className="rpt-hsearch-row">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
          <input value={q} onChange={(e) => { setQ(e.target.value); setPendingName(""); }}
            placeholder="기업명이나 키워드 검색" aria-label="리포트 대상 검색" />
          <button className="rpt-run" type="button" disabled={hits.length === 0}
            onClick={() => hits[0] && pickCompany(hits[0])}>리포트 만들기</button>
        </div>
        {q.trim() !== "" && (
          <div className="rpt-pop">
            {hits.map((h) => (
              <button key={h.symbol} className="rpt-pop-item" type="button" onClick={() => pickCompany(h)}>
                <span className="nm">
                  <b>{h.name}</b>
                  <span>{[h.industry, h.ceo ? `대표 ${h.ceo}` : "", h.listed ? "" : h.symbol, h.listed ? "" : h.audit].filter(Boolean).join(" · ") || h.symbol}</span>
                  {!h.listed && h.address && <span>{h.address}</span>}
                </span>
                <span className={`rpt-badge${h.listed ? " listed" : ""}`}>{h.listed ? "상장" : "비상장"}</span>
              </button>
            ))}
            {searching && hits.length === 0 && <div className="rpt-pop-none">검색 중…</div>}
            {!searching && (
              <button className="rpt-pop-item kw" type="button" onClick={() => pickKeyword(q.trim())}>
                <span className="nm"><b>『{q.trim()}』 키워드로 만들기</b><span>산업·테마 리포트 — 관련 기업군을 스크리닝해요</span></span>
                <span className="rpt-badge">키워드</span>
              </button>
            )}
          </div>
        )}
        {pendingName && (
          <div className="rpt-pending">
            <span><b>{pendingName}</b> 리포트 생성은 다음 업데이트에서 열려요. 완성 샘플로 결과물 형식을 확인해 보세요.</span>
            <button type="button" onClick={() => openPreview(SAMPLES[2])}>샘플 보기</button>
          </div>
        )}
        <div className="rpt-quick">
          {chips.map((c) => (
            <Chip key={c} onClick={() => { setPendingName(""); setQ(c); }}>{c}</Chip>
          ))}
        </div>
      </div>

      <div className="rpt-sec">
        <div className="rpt-h2">만드는 방식</div>
        <div className="rpt-how">
          {HOW.map((h) => (
            <div className="rpt-how-card" key={h.t}>
              <div className="hd">
                <span>{h.t}</span>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">{h.icon}</svg>
              </div>
              <p>{h.d}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="rpt-sec">
        <div className="rpt-h2">이런 리포트가 나와요</div>
        <div className="rpt-h2-sub">실제 데이터로 만든 리포트 — 누르면 바로 PDF가 열려요</div>
        <div className="rpt-samples">
          {SAMPLES.map((sm) => {
            const cover = samplePages(sm.key, 1)[0];
            return (
              <button key={sm.key} className="rpt-sample" type="button" onClick={() => openPreview(sm)}>
                {cover
                  ? <img src={cover} alt={`${sm.subject} 리포트 표지`} loading="lazy" />
                  : <div style={{ aspectRatio: "210/297", background: KIND_DEEP[sm.kind] }} />}
                <span className="rpt-meta"><b>{sm.subject}</b>{KIND_LABEL[sm.kind]} 리포트 · {sm.pages}p</span>
              </button>
            );
          })}
        </div>
      </div>

      {preview && (() => {
        const ppdf = samplePdf(preview.key);
        return ppdf && (
          <>
            <div className="rpt-pdfdim" onClick={() => setPreview(null)} />
            <div className="rpt-pdfmodal" role="dialog" aria-label={`${preview.subject} 리포트`}>
              <div className="rpt-pdfmodal-head">
                <div className="rpt-pdfmodal-meta">
                  <b>{preview.subject}</b>
                  <span>{KIND_LABEL[preview.kind]} 리포트 · {preview.pages}p · {preview.asof}</span>
                </div>
                <a className="rpt-pdfmodal-tab" href={ppdf} target="_blank" rel="noreferrer" aria-label="새 탭에서 열기">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h6v6" /><path d="M10 14 21 3" /><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" /></svg>
                </a>
                <button className="rpt-pdfmodal-close" type="button" onClick={() => setPreview(null)} aria-label="닫기">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
                </button>
              </div>
              <div className="rpt-pdfmodal-body"><PdfViewer src={ppdf} /></div>
            </div>
          </>
        );
      })()}

      {recents.length > 0 && (
        <div className="rpt-sec">
          <div className="rpt-h2">내 리포트</div>
          <div className="rpt-h2-sub">만들었던 보고서를 다시 열거나, 최신 데이터로 새로 만들 수 있어요</div>
          <div className="rpt-mine">
            {recents.map((r) => {
              const sm = r.key ? SAMPLES.find((x) => x.key === r.key) : null;
              const saved = sm ? null : docFromRecent(r);
              const openSaved = () => {
                if (sm) { openPreview(sm); return; }
                if (saved) { setDoc(saved); setThread([]); setScreen("view"); setPdfOpen(false); window.scrollTo(0, 0); }
              };
              const regen = () => {
                if (r.symbol) void startRealGen({ symbol: r.symbol, name: r.subject, listed: !!r.listed });
              };
              return (
                <div key={(r.symbol || r.key) + r.created} className="rpt-mine-row" role="group">
                  <span className="rpt-kind-tag">{KIND_LABEL[r.kind]}</span>
                  <b>{r.subject}</b>
                  <time>
                    {new Date(r.created).toLocaleDateString("ko-KR", { month: "short", day: "numeric" })}
                    {r.asof ? ` · 기준일 ${r.asof}` : ""}
                  </time>
                  <span className="rpt-mine-acts">
                    {(sm || saved) && (
                      <button className="rpt-mine-act" type="button" onClick={openSaved}>보기</button>
                    )}
                    {!sm && r.symbol && (
                      <button className="rpt-mine-act pri" type="button" onClick={regen}>새로 만들기</button>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  // ── 생성 중 — 서버 step 을 타임라인에 반영 ──
  const gen = (
    <div className="rpt-gen">
      <h2>{genName} 리포트를 만들고 있어요</h2>
      <p>모든 수치는 DeepSearch Finorma에서 직접 산출됩니다. 1분 안에 완성돼요.</p>
      <div className="rpt-tl">
        {GEN_STEPS.map(([t, d], i) => (
          <div key={t} className={`rpt-step${!genError && i === genStep ? " run" : i < genStep ? " done" : ""}`}>
            <span className="rpt-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg></span>
            <span className="rpt-tx"><b>{t}</b><span>{i <= genStep ? d : ""}</span></span>
          </div>
        ))}
      </div>
      {genError && (
        <div className="rpt-pending" role="alert" style={{ marginTop: 20 }}>
          <span>{genError}</span>
          <button type="button" onClick={() => { setGenError(""); setScreen("home"); }}>홈으로</button>
        </div>
      )}
      {!genError && (
        <button className="rpt-cancel" type="button" onClick={() => { stopPoll(); setScreen("home"); }}>홈으로</button>
      )}
    </div>
  );

  // ── 뷰어 — PC 는 네이티브 PDF(iframe), 모바일은 표지/버튼 + 전체화면 모달 ──
  const view = doc && (
    <div className="rpt-wrap rpt-wrap-view">
      <div className="rpt-view">
        {!mobile && (
          <div className="rpt-pdfbox">
            <iframe className="rpt-pdf-frame" title={`${doc.subject} 리포트 원문`} src={doc.pdfUrl} />
          </div>
        )}
        <div className="rpt-rail">
          {mobile && (
            <button className="rpt-cover-open" type="button" onClick={() => setPdfOpen(true)}>
              {doc.cover
                ? <img src={doc.cover} alt={`${doc.subject} 리포트 표지`} />
                : <span className="rpt-cover-blank" style={{ background: KIND_DEEP[doc.kind] }}><b>{doc.subject}</b><span>기업조사 리포트 · {doc.asof}</span></span>}
              <span className="rpt-cover-cta">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6" /></svg>
                PDF 미리보기 · {doc.pages}p
              </span>
            </button>
          )}
          <div className="rpt-vmeta">
            <span>분량 <b>{doc.pages}p</b></span>
            <span>유형 <b>{KIND_LABEL[doc.kind]} 리포트</b></span>
            <span>기준일 <b>{doc.asof}</b></span>
          </div>
          <div className="rpt-toc">
            {doc.toc.map(([t, p]) => (
              <div key={t} className="rpt-toc-r"><b>{t}</b><span>{p}</span></div>
            ))}
          </div>
          <div className="rpt-vactions">
            <a className="rpt-va pri" href={doc.saveUrl} download={`${doc.subject}_리포트.pdf`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><path d="M7 10l5 5 5-5" /><path d="M12 15V3" /></svg>
              PDF로 저장
            </a>
            <button className="rpt-va" type="button" onClick={() => setScreen("home")}>목록으로</button>
            <a className="rpt-va" href={doc.pdfUrl} target="_blank" rel="noreferrer">새 탭에서 열기</a>
          </div>
          <div className="rpt-ask">
            <h3>리포트에 대해 이어서 대화할 수 있어요</h3>
            <div className="rpt-thread">
              <div className="rpt-msg agent">
                더 조사해 넣고 싶은 내용이나 고치고 싶은 섹션이 있으면 남겨 주세요.
                <div className="rpt-chips">
                  <button className="rpt-chip" type="button" onClick={() => sendAsk("경쟁사 비교를 더 깊게 해줘")}>경쟁사 비교 더 깊게</button>
                  <button className="rpt-chip" type="button" onClick={() => sendAsk("리스크 분석을 넣어줘")}>리스크 분석 추가</button>
                </div>
              </div>
              {thread.map((m, i) => <div key={i} className={`rpt-msg ${m.who}`}>{m.text}</div>)}
            </div>
            <div className="rpt-askbar">
              <input value={ask} onChange={(e) => setAsk(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") sendAsk(ask); }}
                placeholder="예: 설비투자 추이도 넣어줘" aria-label="리포트 요청 입력" />
              <button type="button" onClick={() => sendAsk(ask)} aria-label="보내기">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5M5 12l7-7 7 7" /></svg>
              </button>
            </div>
          </div>
        </div>
      </div>
      {pdfOpen && mobile && (
        <div className="rpt-pdfmodal" role="dialog" aria-label={`${doc.subject} 리포트 원문`}>
          <div className="rpt-pdfmodal-head">
            <div className="rpt-pdfmodal-meta">
              <b>{doc.subject}</b>
              <span>{KIND_LABEL[doc.kind]} 리포트 · {doc.pages}p</span>
            </div>
            <a className="rpt-pdfmodal-tab" href={doc.pdfUrl} target="_blank" rel="noreferrer" aria-label="새 탭에서 열기">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h6v6" /><path d="M10 14 21 3" /><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" /></svg>
            </a>
            <button className="rpt-pdfmodal-close" type="button" onClick={() => setPdfOpen(false)} aria-label="닫기">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
            </button>
          </div>
          <div className="rpt-pdfmodal-body"><PdfViewer src={doc.pdfUrl} /></div>
        </div>
      )}
    </div>
  );

  return (
    <>
      <header className="lab-top">
        {screen !== "home" && (
          <button className="lab-back" type="button" onClick={() => { stopPoll(); setScreen("home"); }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
            리포트 홈
          </button>
        )}
        <h1>{screen === "view" && doc ? doc.subject : screen === "gen" ? "리포트 생성" : "리포트"}</h1>
        <span className="lab-sub">{screen === "view" && doc ? `${KIND_LABEL[doc.kind]} 리포트` : "기업 · 산업 · 테마 분석"}</span>
      </header>
      <div className="rpt-scroll">
        {screen === "home" && home}
        {screen === "gen" && gen}
        {screen === "view" && view}
      </div>
    </>
  );
}
